/**
 * Prefetch intelligent des routes critiques.
 * Diffère le chargement des chunks ET des données jusqu'au hover ou après idle.
 * N'affecte pas l'UI, le routing ni le code splitting existant.
 */

import { queryClient } from '@/App';
import { orderService } from '@/services/orderService';
import { orderService2 } from '@/services/orderService2';
import { invoiceService } from '@/services/invoiceService';
import { creditNoteService } from '@/services/creditNoteService';
import { quoteService } from '@/services/quoteService';
import { clientService } from '@/services/api';
import { articleService } from '@/services/api';
import { fournisseurService } from '@/services/fournisseurService';
import { fetchPurchaseInvoices } from '@/pages/business/AchatsPage';

export type PrefetchableRoute =
  | 'home'
  | 'bons-de-commande'
  | 'bons-de-commande2'
  | 'order-form2'
  | 'factures'
  | 'notes-de-credit'
  | 'devis'
  | 'clients'
  | 'fournisseurs'
  | 'articles'
  | 'achats';

const ROUTE_IMPORTS: Record<PrefetchableRoute, () => Promise<unknown>> = {
  home: () => import('@/pages/HomeLuxori'),
  'bons-de-commande': () => import('@/pages/business/BonsDeCommandePage'),
  'bons-de-commande2': () => import('@/pages/business/BonsDeCommandePage2'),
  'order-form2': () => import('@/pages/business/OrderFormPage2'),
  factures: () => import('@/pages/business/FacturesPage'),
  'notes-de-credit': () => import('@/pages/business/CreditNotesPage'),
  devis: () => import('@/pages/business/DevisPage'),
  clients: () => import('@/pages/business/ClientsPage'),
  fournisseurs: () => import('@/pages/business/FournisseursPage'),
  articles: () => import('@/pages/business/ArticlesPage'),
  achats: () => import('@/pages/business/AchatsPage'),
};

const prefetched = new Set<PrefetchableRoute>();

/** Prefetch des données par route */
const DATA_PREFETCH: Partial<Record<PrefetchableRoute, () => void>> = {
  'bons-de-commande': () => {
    queryClient.prefetchQuery({
      queryKey: ['orders', 1, 20, '', undefined],
      queryFn: () => orderService.getOrdersPaginated(1, 20, ''),
      staleTime: 30_000,
    });
  },
  'bons-de-commande2': () => {
    queryClient.prefetchQuery({
      queryKey: ['orders2', 1, 20, '', undefined],
      queryFn: () => orderService2.getOrdersPaginated(1, 20, ''),
      staleTime: 30_000,
    });
  },
  factures: () => {
    queryClient.prefetchQuery({
      queryKey: ['invoices', 1, 20, '', undefined],
      queryFn: () => invoiceService.getInvoicesPaginated(1, 20, ''),
      staleTime: 30_000,
    });
  },
  'notes-de-credit': () => {
    queryClient.prefetchQuery({
      queryKey: ['credit-notes', '', undefined],
      queryFn: () => creditNoteService.getAllCreditNotes(),
      staleTime: 30_000,
    });
  },
  devis: () => {
    queryClient.prefetchQuery({
      queryKey: ['quotes', undefined],
      queryFn: () => quoteService.getQuotes(),
      staleTime: 30_000,
    });
  },
  clients: () => {
    queryClient.prefetchQuery({
      queryKey: ['clients', 1, 20, ''],
      queryFn: () => clientService.getClientsPaginated(1, 20, ''),
      staleTime: 30_000,
    });
  },
  fournisseurs: () => {
    queryClient.prefetchQuery({
      queryKey: ['fournisseurs', 1, 20, ''],
      queryFn: () => fournisseurService.getFournisseursPaginated(1, 20, ''),
      staleTime: 30_000,
    });
  },
  articles: () => {
    queryClient.prefetchQuery({
      queryKey: ['articles', 1, 20, ''],
      queryFn: () => articleService.getArticlesPaginated(1, 20, ''),
      staleTime: 30_000,
    });
  },
  achats: () => {
    queryClient.prefetchQuery({
      queryKey: ['achats', 1, 20, '', 'ALL'],
      queryFn: () => fetchPurchaseInvoices(1, 20, '', 'ALL'),
      staleTime: 30_000,
    });
  },
};

const dataPrefetched = new Set<PrefetchableRoute>();

/**
 * Précharge le chunk d'une route + ses données si pas déjà fait.
 */
export function prefetchRoute(route: PrefetchableRoute): void {
  if (prefetched.has(route)) return;
  prefetched.add(route);
  ROUTE_IMPORTS[route]().catch(() => {
    prefetched.delete(route);
  });
  // Prefetch données (une seule fois par session)
  if (!dataPrefetched.has(route) && DATA_PREFETCH[route]) {
    dataPrefetched.add(route);
    DATA_PREFETCH[route]!();
  }
}

/**
 * Prefetch par chemin de route (pour hover sur liens du menu).
 */
const PATH_TO_ROUTE: Record<string, PrefetchableRoute | PrefetchableRoute[]> = {
  '/': 'home',
  '/clients': 'clients',
  '/articles': 'articles',
  '/fournisseurs': 'fournisseurs',
  '/devis': 'devis',
  '/business/bons-de-commande': 'bons-de-commande',
  '/business/bons-de-commande2': ['bons-de-commande2', 'order-form2'],
  '/business/bons-de-commande2/nouveau': 'order-form2',
  '/business/factures': 'factures',
  '/business/notes-de-credit': 'notes-de-credit',
  '/business/achats': 'achats',
};

export function prefetchByPath(path: string): void {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const routeOrRoutes = PATH_TO_ROUTE[normalized];
  if (!routeOrRoutes) return;
  const routes = Array.isArray(routeOrRoutes) ? routeOrRoutes : [routeOrRoutes];
  routes.forEach(r => prefetchRoute(r));
}

const CRITICAL_ROUTES: PrefetchableRoute[] = [
  'home',
  'bons-de-commande',
  'bons-de-commande2',
  'order-form2',
  'factures',
  'notes-de-credit',
  'devis',
  'clients',
  'fournisseurs',
  'articles',
  'achats',
];

/**
 * Planifie le prefetch des routes critiques après idle.
 */
export function scheduleIdlePrefetch(): void {
  const run = () => {
    CRITICAL_ROUTES.forEach(r => prefetchRoute(r));
  };

  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(run, { timeout: 3000 });
  } else {
    setTimeout(run, 2000);
  }
}
